import React, {useState, useContext} from 'react';
import { BarChart } from '@mui/x-charts/BarChart';

// material-ui
import { Card, CardContent, Chip, Dialog, Divider, DialogContent, DialogTitle, Grid, Link, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from '@mui/material/';
import { Close, InsertCommentOutlined } from '@mui/icons-material/';
import Badge, { BadgeProps } from '@mui/material/Badge';
import { styled } from '@mui/material/styles';

//components import
import ArcherIssueListing from '../dataObjects/ArcherIssueListing';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import AlertInfo from '../../../dataObjects/AlertInfo';
import { AuditorCommunicationContext } from '../view-archer';


interface Props {
    chartNames?: string[];
    chartValues?: number[];
}

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
    '& .MuiBadge-badge': {
        right: -3,
        top: 6,
        border: `2px solid ${theme.palette.background.paper}`,
        padding: '0 4px',
    },
}));

const DashboardIssuesChart = (props: Props) => {

    //initial state set up
    const [isIssueListDialogOpen, setIssueListDialogOpen] = useState(false);
    const [archerIssues, setArcherIssues] = useState<ArcherIssueListing[]>(); 
    const [currentStatus, setCurrentStatus] = useState("");

    const context = useContext(AuditorCommunicationContext);

    const setCurrentLetterCpfId = (cpfId: number) => {
        context.setCurrentLetterCpfId(cpfId);
    }

    const barColors = props.chartNames?.map((name: string) => {
        switch (name) {
            case 'Punted':
                return 'blue';
            case 'Referred':
                return 'green';
            case 'Draft':
                return 'yellow';
            case 'Outstanding':
                return 'pink';
            case 'Delinquent':
                return 'red';
        };
    });

    const displayIssueListingDialog = (event:any, data: any) => {
        let status = data.axisValue;
        if (status) {
            setCurrentStatus(status);
            getIssueList(status);
        }
    }

    const getIssueList = (status: string) => {

        let url = `/Archer3/GetDashboardIssueListings?status=${status}&auditorId=${context.currentAuditorId}`;
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetDashboardIssueListings',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setArcherIssues(result.result);
                    setIssueListDialogOpen(true);
                }
            })
    }

    const displayIssueStatusIcon = (status: string) => {
        let shortStatus = status;
        if (status.indexOf(",") > 0) {
            shortStatus = status.substring(0, status.indexOf(","));
        }

        switch (shortStatus) {
            case 'Delinquent':
                return (
                    <Chip variant="outlined" label="Delinquent" size="small" color="error" />
                );
            case 'Outstanding':
                return (
                    <Chip variant="outlined" label="Outstanding" size="small" color="error" />
                );
            case 'Held':
                return (
                    <Chip variant="outlined" label={status} size="small" color="primary" />
                );
            case 'Referred':
                return (
                    <Chip variant="outlined" label={status} size="small" color="primary" />
                );
            case 'Resolved':
                return (
                    <Chip variant="outlined" label={status} size="small" color="success" />
                );
            default:
                return '';
        }
    }

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    return (
        <div>
            <Card className="dashboard-tile">
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Grid container alignContent="center" justifyContent="space-between">
                                <Grid item xs={8}>
                                    <Typography variant="subtitle1" color="blue">Audit Issues Status</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container alignContent="center" justifyContent="space-between">
                                {
                                    props.chartValues &&
                                    <BarChart
                                        series={[
                                            { data: props.chartValues },
                                        ]}
                                        xAxis={[
                                            {
                                                data: props.chartNames,
                                                scaleType: 'band',
                                                colorMap: {
                                                    type: "ordinal",
                                                    values: props.chartNames,
                                                    colors: ["#e15759", "#e53935", "#90caf9", "#1976D2", "#2E7D32"]
                                                }
                                            }
                                        ]}
                                        width={500}
                                        height={270}
                                        onAxisClick={(event, d) => displayIssueListingDialog(event,d)}
                                    />
                                }
                            </Grid>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            {/*Issue Listing*/}
            <Dialog
                open={isIssueListDialogOpen}
                onClose={() => setIssueListDialogOpen(false)}
                scroll={'paper'}
                maxWidth={'md'}
            >
                <DialogTitle>{currentStatus} Issues {archerIssues && `(#${archerIssues.length})`} ...</DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={()=>setIssueListDialogOpen(false)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <Close />
                </IconButton>
                <DialogContent dividers sx={{ maxHeight: '85%' }}>
                    {
                        archerIssues && archerIssues.length > 0 &&
                        <TableContainer>
                            <Table sx={{ minWidth: 650 }}>
                                <TableBody>
                                    {
                                        archerIssues.map((issue) =>
                                            <TableRow key={issue.issueId}>
                                                <TableCell sx={{ textAlign: "left!important", maxWidth: "300px" }}>
                                                    <Typography className="is-clickable" variant="body2" onClick={() => setCurrentLetterCpfId(issue.cpfId)}><b>{issue.cpfId} {issue.filerFullName}</b></Typography>
                                                </TableCell>
                                                <TableCell sx={{ textAlign: "left!important" }}>
                                                    <Stack direction="row" spacing={2} key={issue.issueId}>
                                                        {displayIssueStatusIcon(issue.status)}
                                                        <span>
                                                            <span>{issue.issueTypeCode} {issue.header}</span>
                                                            <Link onClick={() => setCurrentLetterCpfId(issue.cpfId)} key={issue.letterId}> (<i>sent {issue.date}, {issue.flaggedItemCount} items</i>)</Link>
                                                        </span>
                                                        {issue.pendingResponseCount > 0 &&
                                                            <StyledBadge badgeContent={issue.pendingResponseCount} color="error" onClick={() => setCurrentLetterCpfId(issue.cpfId)}>
                                                                <InsertCommentOutlined className="is-clickable" color="action" />
                                                            </StyledBadge>
                                                        }
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    }
                   
                </DialogContent>
                <Divider light sx={{ marginTop: 2 }} />
            </Dialog>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </div>
    )
}

export default DashboardIssuesChart;